import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  Response,
  PermissionEnum,
  apiAbortController,
  Country,
  Currency,
} from '@ui/src';
import { Account, PaymentType } from './types';
import api from '../../api/api';

export interface PermissionReponse {
  permissions: PermissionEnum[];
}

export const configurationDictionariesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getConfigurationDictionaries: build.query<any, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const dictionaries = await Promise.all([
          fetchWithBQ('/v1/risk-score-ranges?all=true'),
        ]);

        return dictionaries
          ? { data: { riskScoreRanges: dictionaries[0].data } }
          : { error: { status: 'FETCH_ERROR' } as FetchBaseQueryError };
      },
    }),
    getConfigurationCountries: build.query<Response<Country>, void>({
      query: () => ({
        url: '/v1/countries?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getConfigurationDictionariesTransactionTypes: build.query<
      Response<{ name: string; id: string }>,
      void
    >({
      query: () => ({
        url: '/v1/transactions-types?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getDictionariesConfigurationCurrencies: build.query<
      Response<Currency>,
      void
    >({
      query: () => ({
        url: '/v1/currencies?active=true&all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getConfigurationBaseCurrency: build.query<{ data: Currency }, void>({
      query: () => ({
        url: '/v1/currencies/base',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getConfigurationAccounts: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getConfigurationPermissions: build.query<{ data: PermissionReponse }, void>(
      {
        query: () => ({
          url: '/v1/employees/whoami',
          signal: apiAbortController.getSignalToken(),
        }),
      },
    ),
    getConfigurationWhitelabel: build.query<
      { data: { data: { value: Record<string, string> } } },
      void
    >({ query: () => ({ url: '/v1/settings/whitelabel' }) }),
    getConfigurationPaymentTypes: build.query<Response<PaymentType>, void>({
      query: () => ({
        url: '/v1/payment-categories?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getConfigurationSettings: build.query<
      Response<{ key: string; data: { value: boolean } }>,
      void
    >({
      query: () => ({
        url: '/v1/settings?take=9999',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetConfigurationDictionariesQuery,
  useLazyGetConfigurationDictionariesQuery,
  useLazyGetConfigurationAccountsQuery,
  useLazyGetConfigurationPermissionsQuery,
  useLazyGetConfigurationWhitelabelQuery,
  useLazyGetConfigurationPaymentTypesQuery,
  useLazyGetConfigurationBaseCurrencyQuery,
  useLazyGetConfigurationCountriesQuery,
  useLazyGetDictionariesConfigurationCurrenciesQuery,
  useLazyGetConfigurationSettingsQuery,
  useLazyGetConfigurationDictionariesTransactionTypesQuery,
} = configurationDictionariesApi;

export const {
  endpoints: {
    getConfigurationDictionaries,
    getConfigurationAccounts,
    getConfigurationPermissions,
    getConfigurationPaymentTypes,
    getConfigurationBaseCurrency,
    getConfigurationCountries,
    getDictionariesConfigurationCurrencies,
    getConfigurationSettings,
    getConfigurationDictionariesTransactionTypes,
  },
} = configurationDictionariesApi;
